import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Trophy, TrendingUp, TrendingDown } from "lucide-react";

export default function AdminTestsV2() {
  const [clients, setClients] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [selected, setSelected] = useState("");
  const [selectedProto, setSelectedProto] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [evolution, setEvolution] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), protocol_key: "", attempts: [{ value: "", valid: true }], conditions: "", video_url: "", marker: "current", notes: "" });

  useEffect(() => {
    Promise.all([api.get("/admin/clients"), api.get("/admin/test-protocols")]).then(([c, p]) => {
      setClients(c.data); setProtocols(p.data);
    });
  }, []);

  const load = async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/admin/test-assessments-v2/${selected}`);
      setAssessments(data);
      if (selectedProto) {
        const { data: ev } = await api.get(`/admin/test-assessments-v2/${selected}/evolution/${selectedProto}`);
        setEvolution(ev);
      } else { setEvolution(null); }
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); }, [selected, selectedProto]);

  const create = async () => {
    if (!selected || !form.protocol_key) return toast.error("Cliente y protocolo requeridos");
    const attempts = form.attempts.filter((a) => a.value !== "").map((a) => ({ ...a, value: parseFloat(a.value) }));
    if (attempts.length === 0) return toast.error("Al menos un intento válido");
    try {
      await api.post("/admin/test-assessments-v2", { client_id: selected, protocol_key: form.protocol_key, date: form.date, attempts, conditions: form.conditions || null, video_url: form.video_url || null, marker: form.marker, notes: form.notes || null });
      toast.success("Test registrado");
      setShowForm(false);
      setForm({ ...form, attempts: [{ value: "", valid: true }], notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const proto = protocols.find((p) => p.key === selectedProto);

  return (
    <AppShell>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Tests V2 · Batería QV</h1>
        <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Sprint 10m/20m, Salto horizontal, CMJ y movilidades. Múltiples intentos con mejor resultado.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 20 }}>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} style={inputStyle} data-testid="tests-v2-client">
          <option value="">— Cliente —</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={selectedProto} onChange={(e) => setSelectedProto(e.target.value)} style={inputStyle} data-testid="tests-v2-proto">
          <option value="">— Ver evolución de… —</option>
          {protocols.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
        </select>
        {selected && <button onClick={() => { setShowForm(true); setForm({ ...form, protocol_key: selectedProto || protocols[0]?.key || "" }); }} style={btnPrimary} data-testid="tests-v2-new"><Plus size={14}/> Nuevo test</button>}
      </div>

      {evolution && evolution.pb && (
        <div style={{ background: "linear-gradient(90deg, rgba(250,204,21,0.1), transparent)", border: "1px solid rgba(250,204,21,0.3)", padding: 18, borderRadius: 4, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "#facc15", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
            <Trophy size={14}/> Personal Best — {evolution.protocol.name}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14 }}>
            <Stat label="Inicial" value={evolution.initial?.best} unit={evolution.protocol.unit} date={evolution.initial?.date} />
            <Stat label="PB" value={evolution.pb.best} unit={evolution.protocol.unit} date={evolution.pb.date} highlight="#facc15" />
            <Stat label="Actual" value={evolution.current?.best} unit={evolution.protocol.unit} date={evolution.current?.date} />
            <div>
              <div style={{ fontSize: 11, color: "#a3a3a3", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>Mejora</div>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, color: (evolution.improvement_pct || 0) > 0 ? "#4ade80" : "#f87171", lineHeight: 1, display: "flex", alignItems: "center", gap: 4 }}>
                {(evolution.improvement_pct || 0) > 0 ? <TrendingUp size={22}/> : <TrendingDown size={22}/>}
                {evolution.improvement_pct != null ? `${evolution.improvement_pct > 0 ? "+" : ""}${evolution.improvement_pct}%` : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && selected && (
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.14)", padding: 20, borderRadius: 4, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase" }}>Registrar test</h3>
            <button onClick={() => setShowForm(false)} style={{ background: "transparent", border: "none", color: "#a3a3a3", cursor: "pointer" }}><X size={18}/></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
            <select value={form.protocol_key} onChange={(e) => setForm({ ...form, protocol_key: e.target.value })} style={inputStyle} data-testid="test-v2-form-proto">
              <option value="">— Protocolo —</option>
              {protocols.map((p) => <option key={p.key} value={p.key}>{p.name} ({p.unit}, {p.direction === "LOWER_IS_BETTER" ? "menos = mejor" : "más = mejor"})</option>)}
            </select>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} />
            <select value={form.marker} onChange={(e) => setForm({ ...form, marker: e.target.value })} style={inputStyle}>
              <option value="initial">Inicial</option>
              <option value="intermediate">Intermedio</option>
              <option value="current">Actual</option>
            </select>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Intentos</label>
            {form.attempts.map((a, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "80px auto 1fr auto", gap: 6, marginBottom: 4, alignItems: "center" }}>
                <input type="number" step="0.01" value={a.value} onChange={(e) => { const na = [...form.attempts]; na[i].value = e.target.value; setForm({ ...form, attempts: na }); }} placeholder="Valor" style={inputStyle} data-testid={`test-v2-attempt-${i}`} />
                <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12, color: "#a3a3a3" }}>
                  <input type="checkbox" checked={a.valid} onChange={(e) => { const na = [...form.attempts]; na[i].valid = e.target.checked; setForm({ ...form, attempts: na }); }} /> válido
                </label>
                <input value={a.invalid_reason || ""} onChange={(e) => { const na = [...form.attempts]; na[i].invalid_reason = e.target.value; setForm({ ...form, attempts: na }); }} placeholder="Motivo si no válido" style={inputStyle} disabled={a.valid} />
                <button onClick={() => setForm({ ...form, attempts: form.attempts.filter((_, j) => j !== i) })} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#a3a3a3", padding: "4px 8px", cursor: "pointer", borderRadius: 2 }}>−</button>
              </div>
            ))}
            <button onClick={() => setForm({ ...form, attempts: [...form.attempts, { value: "", valid: true }] })} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#e5e5e5", padding: "5px 10px", fontSize: 12, cursor: "pointer", borderRadius: 2, marginTop: 4 }}>+ Intento</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <input value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Condiciones (opcional)" style={inputStyle} />
            <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="URL vídeo (opcional)" style={inputStyle} />
          </div>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas..." style={{ ...inputStyle, width: "100%", resize: "vertical" }} />
          <button onClick={create} style={{ ...btnPrimary, marginTop: 10 }} data-testid="test-v2-save">Guardar</button>
        </div>
      )}

      {assessments.length > 0 && (
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
          {assessments.map((a) => (
            <div key={a.id} style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <b>{a.protocol_name}</b> <span style={{ color: "#a3a3a3", fontSize: 12, marginLeft: 6 }}>{a.date}</span>
                  {a.marker && <span style={{ marginLeft: 8, fontSize: 10, background: "rgba(255,255,255,0.06)", padding: "2px 8px", borderRadius: 999, color: "#a3a3a3", letterSpacing: "0.08em", textTransform: "uppercase" }}>{a.marker}</span>}
                </div>
                <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 22, color: "#dc2626" }}>{a.best}<span style={{ fontSize: 11, color: "#a3a3a3", marginLeft: 3 }}>{a.unit}</span></div>
              </div>
              {a.conditions && <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 4 }}>{a.conditions}</div>}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function Stat({ label, value, unit, date, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#a3a3a3", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, color: highlight || "#fff", lineHeight: 1 }}>{value ?? "—"}<span style={{ fontSize: 12, color: "#a3a3a3", marginLeft: 3 }}>{unit}</span></div>
      {date && <div style={{ fontSize: 10, color: "#737373", marginTop: 3 }}>{date}</div>}
    </div>
  );
}

const inputStyle = { background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", fontSize: 13, borderRadius: 3, outline: "none", boxSizing: "border-box", width: "100%" };
const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 5 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2 };
